import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, PenLine, CheckCircle2, RotateCcw, Sparkles, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TIPO = { rosso: 'Rosso', bianco: 'Bianco', rosato: 'Rosato', bollicine: 'Bollicine', champagne: 'Champagne', prosecco: 'Prosecco', dolce: 'Dolce' };

const EsempioCard = ({ e, i }) => (
  <div className="border rounded-xl p-4 space-y-2 bg-white" data-testid={`descrizioni-esempio-${i}`}>
    <div>
      <p className="text-sm font-semibold text-gray-900">{e.nome}</p>
      <p className="text-xs text-gray-500">{[e.cantina, TIPO[e.tipo_vino] || e.tipo_vino, e.vitigno].filter(Boolean).join(' · ')}</p>
    </div>
    {e.errore ? (
      <p className="text-xs text-red-600" data-testid={`descrizioni-esempio-errore-${i}`}>Non generata: {e.errore}</p>
    ) : (
      <>
        <p className="text-sm leading-relaxed text-gray-800" data-testid={`descrizioni-esempio-testo-${i}`}>{e.descrizione}</p>
        <p className="text-xs text-gray-600"><span className="font-semibold text-amber-800">Abbinamenti:</span> {e.abbinamenti}</p>
      </>
    )}
    {e.descrizione_attuale && (
      <details className="text-xs text-gray-400">
        <summary className="cursor-pointer hover:text-gray-600" data-testid={`descrizioni-esempio-attuale-${i}`}>Descrizione attuale</summary>
        <p className="mt-1 leading-relaxed">{e.descrizione_attuale}</p>
      </details>
    )}
  </div>
);

const ProgressoJob = ({ job }) => {
  const fatti = (job.fatti || 0) + (job.falliti || 0);
  const perc = job.totale ? Math.round((fatti / job.totale) * 100) : 0;
  return (
    <div className="space-y-4" data-testid="descrizioni-progresso">
      <div>
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span data-testid="descrizioni-progresso-conteggio">{job.fatti || 0} scritte su {job.totale}{job.falliti ? ` · ${job.falliti} non riuscite` : ''}</span>
          <span>{perc}%</span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full bg-amber-700 transition-[width] duration-500" style={{ width: `${perc}%` }} />
        </div>
      </div>
      {job.stato === 'completato' && (
        <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="descrizioni-completato"><CheckCircle2 className="h-5 w-5" /> Riscrittura completata.</p>
      )}
      {job.stato === 'interrotto' && (
        <p className="text-sm text-amber-700 flex items-center gap-2" data-testid="descrizioni-interrotto"><AlertTriangle className="h-5 w-5" /> Riscrittura interrotta: puoi riprenderla dai vini mancanti.</p>
      )}
      {(job.esempi || []).slice(-3).reverse().map((e, i) => <EsempioCard key={`${e.nome}-${i}`} e={e} i={`job-${i}`} />)}
      {job.errori?.length > 0 && (
        <details className="text-xs text-red-600" data-testid="descrizioni-errori">
          <summary className="cursor-pointer">{job.errori.length} vini non riscritti</summary>
          <ul className="mt-1 space-y-0.5">{job.errori.map((er, i) => <li key={i}>{er.nome}: {er.errore}</li>)}</ul>
        </details>
      )}
    </div>
  );
};

export const DescrizioniSommelierDialog = ({ open, onClose, getAuthHeaders, onCompletato }) => {
  const [esempi, setEsempi] = useState(null);
  const [totale, setTotale] = useState(0);
  const [job, setJob] = useState(null);
  const [caricando, setCaricando] = useState(false);
  const [vistaJob, setVistaJob] = useState(false);
  const statoPrima = useRef(null);

  const leggiStato = useCallback(async () => {
    const r = await axios.get(`${API}/api/vini-descrizioni/stato`, { headers: getAuthHeaders() });
    const j = r.data.job;
    setJob(j);
    if (j?.stato === 'completato' && statoPrima.current === 'in_corso') {
      toast.success(`Descrizioni riscritte: ${j.fatti} su ${j.totale}`);
      onCompletato?.();
    }
    statoPrima.current = j?.stato;
    return j;
  }, [getAuthHeaders, onCompletato]);

  useEffect(() => {
    if (!open) return;
    setEsempi(null);
    leggiStato().then(j => setVistaJob(['in_corso', 'interrotto'].includes(j?.stato))).catch(() => setJob(null));
  }, [open, leggiStato]);

  useEffect(() => {
    if (!open || job?.stato !== 'in_corso') return undefined;
    const t = setInterval(() => leggiStato().catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [open, job?.stato, leggiStato]);

  if (!open) return null;

  const generaEsempi = async () => {
    setCaricando(true);
    try {
      const escludi = (esempi || []).map(e => e.id);
      const r = await axios.post(`${API}/api/vini-descrizioni/anteprima`, { escludi }, { headers: getAuthHeaders(), timeout: 120000 });
      setEsempi(r.data.esempi);
      setTotale(r.data.totale_vini);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Esempi non generati, riprova');
    } finally {
      setCaricando(false);
    }
  };

  const avvia = async (soloMancanti = false) => {
    setCaricando(true);
    try {
      const r = await axios.post(`${API}/api/vini-descrizioni/avvia`, soloMancanti ? { solo_mancanti: true } : {}, { headers: getAuthHeaders() });
      setEsempi(null);
      setVistaJob(true);
      await leggiStato();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile avviare la riscrittura');
    } finally {
      setCaricando(false);
    }
  };

  const inCorso = job?.stato === 'in_corso';
  const mostraJob = job && !esempi && vistaJob;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="descrizioni-dialog">
      <div className="bg-white w-full sm:max-w-3xl max-h-[95vh] sm:rounded-2xl rounded-t-2xl flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div>
            <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 text-gray-900"><PenLine className="h-5 w-5 text-amber-700" /> Descrizioni da sommelier</h2>
            <p className="text-xs text-gray-500 mt-0.5">Riscrive le descrizioni di tutta la carta usando solo i dati verificati (vitigno, denominazione, zona): 2-3 frasi, nessuna annata e nessun dato inventato. Prima ti mostro 3 esempi da approvare.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="descrizioni-chiudi"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-stone-50/60">
          {caricando && !esempi && <p className="py-10 text-center text-sm text-gray-500 flex items-center justify-center gap-2" data-testid="descrizioni-caricamento"><Loader2 className="h-4 w-4 animate-spin" /> Il sommelier sta scrivendo…</p>}
          {!caricando && !esempi && !mostraJob && (
            <div className="py-8 text-center space-y-3" data-testid="descrizioni-intro">
              <p className="text-sm text-gray-600">Genero 3 esempi (un rosso, un bianco e una bollicina) senza salvare nulla.</p>
              {job?.stato === 'completato' && <p className="text-xs text-gray-500" data-testid="descrizioni-ultimo-job">Ultima riscrittura: {job.fatti} su {job.totale} vini.</p>}
            </div>
          )}
          {esempi && !esempi.length && <p className="py-10 text-center text-sm text-gray-500" data-testid="descrizioni-nessun-vino">Nessun vino in carta da descrivere.</p>}
          {esempi && esempi.map((e, i) => <EsempioCard key={e.id} e={e} i={i} />)}
          {mostraJob && <ProgressoJob job={job} />}
        </div>

        <div className="px-5 py-3 border-t flex flex-wrap items-center justify-end gap-2">
          {job?.stato === 'interrotto' && !esempi && (
            <button onClick={() => avvia(true)} disabled={caricando} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-amber-800 border border-amber-300 hover:bg-amber-50 disabled:opacity-50" data-testid="descrizioni-riprendi">
              <RotateCcw className="h-4 w-4" /> Riprendi dai mancanti
            </button>
          )}
          {!inCorso && (
            <button onClick={generaEsempi} disabled={caricando} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-gray-700 border hover:bg-gray-50 disabled:opacity-50" data-testid={esempi ? 'descrizioni-altri-esempi' : 'descrizioni-genera-esempi'}>
              {caricando && esempi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {esempi ? 'Altri 3 esempi' : 'Genera 3 esempi'}
            </button>
          )}
          {esempi && (
            <button onClick={() => avvia(false)} disabled={caricando || !esempi.some(e => !e.errore)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-700 text-white text-sm font-medium hover:bg-amber-800 disabled:opacity-50" data-testid="descrizioni-approva">
              <CheckCircle2 className="h-4 w-4" /> Approva e riscrivi tutte ({totale})
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
